/**
 * Browser push, over Firebase Cloud Messaging.
 *
 * The page's own alerts (`desktop-alerts.ts`) only work while a panel tab is
 * open, because it is the page that polls for new notices. Push is the server
 * telling the browser, which reaches an admin with every panel tab closed —
 * as long as Chrome itself is running, in a window or in the tray.
 *
 * Firebase is used only here, to turn the browser's push subscription into an
 * FCM token the API can address. The service worker that receives the pushes
 * (`public/push-sw.js`) is plain Web Push and loads nothing from Firebase, so
 * there is no SDK to fetch from a CDN and no config baked into `public/`.
 *
 * The SDK is imported on first use rather than at the top: sign-in and every
 * page load pull in this module (sign-out needs it), and a browser without
 * permission should not download Firebase at all.
 *
 * Off unless every value below is configured: a build without them behaves
 * exactly as before, with the in-page alerts.
 */

import { panelTimeZone } from "./timezone";

const env = import.meta.env;

const FIREBASE_CONFIG = {
  apiKey: env.VITE_FIREBASE_API_KEY ?? "",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
  projectId: env.VITE_FIREBASE_PROJECT_ID ?? "",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
  appId: env.VITE_FIREBASE_APP_ID ?? "",
};
const VAPID_KEY = env.VITE_FIREBASE_VAPID_KEY ?? "";

export const pushConfigured =
  Object.values(FIREBASE_CONFIG).every((value) => value.trim() !== "") && VAPID_KEY.trim() !== "";

/** The token this browser registered, so sign-out can remove exactly it. */
const TOKEN_KEY = "aa.push-token";

let active = false;

/**
 * True once this browser's token is saved with the API. The page then leaves
 * OS notifications to the service worker, which would otherwise show every
 * notice a second time.
 */
export function isPushActive(): boolean {
  return active;
}

/**
 * Registers the service worker, fetches this browser's FCM token and hands it
 * to `save`. Safe to call on every load: the token is stable, and the API
 * upserts on it, which is also what moves it to a different admin who signs
 * in on the same browser.
 *
 * Never throws. Any failure — no permission, an unsupported browser, FCM
 * unreachable — leaves the in-page alerts in charge, which is what happened
 * before push existed.
 */
export async function registerPush(save: (token: string) => Promise<void>): Promise<boolean> {
  try {
    if (!pushConfigured) return false;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
    if (!("serviceWorker" in navigator)) return false;

    const [{ getApps, initializeApp }, { getMessaging, getToken, isSupported }] = await Promise.all(
      [import("firebase/app"), import("firebase/messaging")],
    );
    if (!(await isSupported())) return false;

    const registration = await navigator.serviceWorker.register(
      `${import.meta.env.BASE_URL}push-sw.js`,
      { scope: import.meta.env.BASE_URL },
    );
    await navigator.serviceWorker.ready;

    const app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
    const token = await getToken(getMessaging(app), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return false;

    await save(token);
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Sign-out then clears every browser of this admin instead of just this one.
    }
    active = true;
    return true;
  } catch {
    active = false;
    return false;
  }
}

/**
 * The token this browser last registered, if any. For a session that must not
 * receive push: removing exactly this token leaves the admin's other browsers
 * alone, where an empty DELETE would clear them all.
 */
export function storedPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Forgets the stored token once the API has removed it. */
export function forgetPushToken(): void {
  active = false;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Retried on the next load; the DELETE is idempotent.
  }
}

/**
 * Removes this browser's token from the API. Called by sign-out *before* the
 * logout request, because logout revokes the session this call authenticates
 * with. Waits at most `timeoutMs` so a slow API cannot hold sign-out hostage.
 */
export async function unregisterPush(
  apiOrigin: string,
  accessToken: string,
  timeoutMs = 1500,
): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Unknown token: fall through and clear every browser this admin holds.
  }
  active = false;
  if (!pushConfigured) return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(`${apiOrigin}/v1/admin/notifications/push-token`, {
      method: "DELETE",
      headers: {
        authorization: `Bearer ${accessToken}`,
        "content-type": "application/json",
        "x-timezone": panelTimeZone(),
      },
      body: JSON.stringify(token ? { token } : {}),
      keepalive: true,
      signal: controller.signal,
    });
  } catch {
    // Best effort. A token left behind is pruned the first time FCM rejects it,
    // or moved when the next admin signs in on this browser.
  } finally {
    clearTimeout(timer);
  }
}
